// TimeTracker — start/stop work timer + time summary for one ticket or project.
// Dropped into the ticket page and the project detail panel. Starting a timer
// anywhere auto-stops whatever else was running, so time is never double-counted.
import { useState, useEffect, useCallback, useRef } from "react";
import { Play, Square, Plus, Clock, Trash2 } from "lucide-react";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (p, o = {}) =>
  fetch(API + p, { headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" }, ...o });

export const fmtDur = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : m ? `${m}m ${String(sec).padStart(2, "0")}s` : `${sec}s`;
};
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function TimeTracker({ entityType, entityId, compact }) {
  const [active, setActive]   = useState(null);   // the globally running session
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tick, setTick]       = useState(0);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState("");
  const [showLog, setShowLog] = useState(false);
  const [manual, setManual]   = useState({ open: false, minutes: "", note: "" });
  const timerRef = useRef(null);

  const mine = active && active.entity_type === entityType && String(active.entity_id) === String(entityId);

  const load = useCallback(async () => {
    try {
      const [a, s, l] = await Promise.all([
        apiFetch("/time/active").then(r => r.json()),
        apiFetch(`/time/summary?entity_type=${entityType}&entity_id=${entityId}`).then(r => r.json()),
        apiFetch(`/time/sessions?entity_type=${entityType}&entity_id=${entityId}`).then(r => r.json()),
      ]);
      setActive(a && a.id ? a : null);
      setSummary(s); setSessions(l.sessions || []);
    } catch { /* transient */ }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  // Live-ticking clock while a timer for THIS entity is running.
  useEffect(() => {
    clearInterval(timerRef.current);
    if (mine) timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [mine]);

  const elapsed = mine ? Math.floor((Date.now() - new Date(active.started_at).getTime()) / 1000) : 0;

  const start = async () => {
    setBusy(true); setErr("");
    try {
      const r = await apiFetch("/time/start", { method: "POST", body: JSON.stringify({ entity_type: entityType, entity_id: entityId }) });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Could not start the timer"); return; }
      if (d.auto_stopped) setErr(`Stopped your previous timer (${fmtDur(d.auto_stopped.duration_seconds)}) and switched here.`);
      await load(); setTick(t => t + 1);
    } finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true); setErr("");
    try {
      const r = await apiFetch("/time/stop", { method: "POST", body: JSON.stringify({}) });
      if (!r.ok) { const d = await r.json(); setErr(d.error || "Could not stop"); return; }
      await load();
    } finally { setBusy(false); }
  };

  const addManual = async () => {
    const mins = Number(manual.minutes);
    if (!mins || mins <= 0) return;
    setBusy(true);
    try {
      await apiFetch("/time/sessions", { method: "POST", body: JSON.stringify({ entity_type: entityType, entity_id: entityId, minutes: mins, note: manual.note || null }) });
      setManual({ open: false, minutes: "", note: "" });
      await load();
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this time entry?")) return;
    const r = await apiFetch(`/time/sessions/${id}`, { method: "DELETE" });
    if (!r.ok) { const d = await r.json(); setErr(d.error || "Could not delete"); return; }
    load();
  };

  const total = summary?.total_seconds || 0;
  const btn = { display: "inline-flex", alignItems: "center", gap: 7, border: "none", borderRadius: 8, padding: compact ? "7px 14px" : "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };

  return (
    <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10, padding: 13 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Clock size={13} /> Time
        </span>
        {mine ? (
          <>
            <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 19, fontWeight: 800, color: "#34d399", letterSpacing: "-0.5px" }}>
              {fmtDur(elapsed)}
            </span>
            <button onClick={stop} disabled={busy} style={{ ...btn, background: "rgba(239,68,68,0.12)", color: "#f87171", border: "1px solid rgba(239,68,68,0.35)" }}>
              <Square size={13} /> Stop
            </button>
          </>
        ) : (
          <button onClick={start} disabled={busy} style={{ ...btn, background: "linear-gradient(135deg,#10b981,#0891b2)", color: "#fff" }}>
            <Play size={13} /> Start timer
          </button>
        )}
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "var(--t-text-primary)" }}>{fmtDur(total)}</div>
          {summary?.total_cents > 0 && <div style={{ fontSize: 11, color: "#34d399", fontWeight: 700 }}>{money(summary.total_cents)} billable</div>}
        </div>
      </div>

      {active && !mine && (
        <div style={{ marginTop: 8, fontSize: 11.5, color: "#fbbf24" }}>
          ⏱ You're currently timing <b>{active.label}</b> — starting here will switch it.
        </div>
      )}
      {err && <div style={{ marginTop: 8, fontSize: 11.5, color: err.startsWith("Stopped") ? "#94a3b8" : "#f87171" }}>{err}</div>}

      {/* Per-person breakdown */}
      {summary?.by_agent?.length > 0 && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
          {summary.by_agent.map(a => (
            <div key={a.agent_id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
              <span style={{ flex: 1, color: "var(--t-text-body)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.agent_name}</span>
              <span style={{ color: "#94a3b8", fontFamily: "ui-monospace, monospace" }}>{fmtDur(a.seconds)}</span>
              {a.amount_cents > 0 && <span style={{ color: "#34d399", fontWeight: 700, width: 70, textAlign: "right" }}>{money(a.amount_cents)}</span>}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={() => setManual(m => ({ ...m, open: !m.open }))}
          style={{ background: "none", border: "none", color: "#60a5fa", fontSize: 11.5, cursor: "pointer", padding: 0, fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Plus size={12} /> Add time manually
        </button>
        {sessions.length > 0 && (
          <button onClick={() => setShowLog(s => !s)}
            style={{ background: "none", border: "none", color: "#64748b", fontSize: 11.5, cursor: "pointer", padding: 0, fontFamily: "inherit", marginLeft: "auto" }}>
            {showLog ? "Hide" : `${sessions.length} entr${sessions.length === 1 ? "y" : "ies"}`}
          </button>
        )}
      </div>

      {manual.open && (
        <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
          <input type="number" min="1" value={manual.minutes} onChange={e => setManual(m => ({ ...m, minutes: e.target.value }))}
            placeholder="min" style={{ width: 70, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "5px 8px", color: "var(--t-text-primary)", fontSize: 12 }} />
          <input value={manual.note} onChange={e => setManual(m => ({ ...m, note: e.target.value }))}
            placeholder="What did you do?" style={{ flex: 1, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "5px 8px", color: "var(--t-text-primary)", fontSize: 12 }} />
          <button onClick={addManual} disabled={busy || !manual.minutes}
            style={{ background: "var(--t-accent-grad)", color: "#fff", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Add</button>
        </div>
      )}

      {showLog && (
        <div style={{ marginTop: 8, borderTop: "1px solid var(--t-border-soft)", paddingTop: 8, display: "flex", flexDirection: "column", gap: 5, maxHeight: 220, overflow: "auto" }}>
          {sessions.map(s => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5 }}>
              <span style={{ color: "#64748b", width: 92, flexShrink: 0 }}>
                {new Date(s.started_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}{" "}
                {new Date(s.started_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </span>
              <span style={{ color: "var(--t-text-body)", fontFamily: "ui-monospace, monospace", width: 62, flexShrink: 0 }}>
                {s.ended_at ? fmtDur(s.duration_seconds) : "running"}
              </span>
              <span style={{ flex: 1, minWidth: 0, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {s.agent_name}{s.note ? ` — ${s.note}` : ""}
              </span>
              {s.invoice_id
                ? <span style={{ fontSize: 10, color: "#34d399", fontWeight: 700 }}>invoiced</span>
                : <button onClick={() => del(s.id)} style={{ background: "none", border: "none", color: "var(--t-border-strong, #475569)", cursor: "pointer", padding: 0, display: "flex" }}><Trash2 size={12} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
